import React, { useEffect, useState } from "react";
import { X, Share2, Plus, Smartphone, ChevronDown } from "lucide-react";

const KEY_INSTALLED = "neuroeq_pwa_installed";
const KEY_DISMISSED = "neuroeq_install_dismissed_at";
const COOLOFF_DAYS = 7;

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches ||
         window.navigator.standalone === true;
}
function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
}
function isAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

// Capture beforeinstallprompt globally as early as possible. Chrome only fires it once.
let CAPTURED_PROMPT = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    CAPTURED_PROMPT = e;
  });
  window.addEventListener("appinstalled", () => {
    localStorage.setItem(KEY_INSTALLED, "1");
    CAPTURED_PROMPT = null;
  });
}

export function PwaInstall({ moment = "auto", forceOpen = false, onClose }) {
  const [deferred, setDeferred] = useState(CAPTURED_PROMPT);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      CAPTURED_PROMPT = e;
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (forceOpen) { setOpen(true); return; }
    if (isStandalone() || localStorage.getItem(KEY_INSTALLED) === "1") return;
    const dismissed = localStorage.getItem(KEY_DISMISSED);
    if (dismissed) {
      const days = (Date.now() - parseInt(dismissed, 10)) / 86400000;
      if (days < COOLOFF_DAYS) return;
    }
    if (moment === "post-session") {
      const t = setTimeout(() => setOpen(true), 3500);
      return () => clearTimeout(t);
    }
  }, [moment, forceOpen]);

  if (!open) return null;

  const close = () => {
    localStorage.setItem(KEY_DISMISSED, String(Date.now()));
    setOpen(false);
    onClose && onClose();
  };

  const tryInstall = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          localStorage.setItem(KEY_INSTALLED, "1");
        }
        setDeferred(null);
        CAPTURED_PROMPT = null;
        setOpen(false);
      } catch {
        // fall through to instructions
      }
    }
  };

  // Determine instructional mode
  const showAndroidPrompt = !!deferred;
  const platform = isIOS() ? "ios" : isAndroid() ? "android" : "desktop";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 fade-in"
         data-testid="pwa-install-modal" onClick={close}>
      <div className="card-elevated w-full max-w-md p-6 sm:p-8 rise-in relative"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted"
                data-testid="pwa-install-close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--neu-orange))]/10 flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-[hsl(var(--neu-orange))]" />
          </div>
          <h3 className="font-display text-2xl">Install NeuroEQ</h3>
        </div>
        <p className="text-muted-foreground mb-5">
          Get faster access, your Sunday reminder, and offline session. right from your home screen.
        </p>

        {showAndroidPrompt ? (
          <p className="text-sm text-muted-foreground mb-6">
            Tap <b>Install</b> and confirm. NeuroEQ will appear on your home screen and launch full-screen.
          </p>
        ) : platform === "ios" ? (
          <IosInstructions />
        ) : platform === "android" ? (
          <AndroidManualInstructions />
        ) : (
          <DesktopInstructions />
        )}

        <div className="flex gap-3 mt-4">
          <button onClick={close} className="btn-ghost flex-1" data-testid="pwa-install-maybe-later">Close</button>
          {showAndroidPrompt && (
            <button onClick={tryInstall} className="btn-primary flex-1"
                    data-testid="pwa-install-confirm">Install</button>
          )}
        </div>
      </div>
    </div>
  );
}

function IosInstructions() {
  return (
    <ol className="space-y-3 mb-2 text-sm">
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">1</span>
        <span className="flex-1">Open this site in <b>Safari</b> (not Chrome on iOS).</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">2</span>
        <span className="flex-1">Tap the <Share2 className="inline w-4 h-4 mx-1"/> <b>Share</b> button.</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">3</span>
        <span className="flex-1">Scroll & tap <Plus className="inline w-4 h-4 mx-1"/> <b>Add to Home Screen</b>.</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">4</span>
        <span>Tap <b>Add</b>. Done!</span>
      </li>
    </ol>
  );
}

function AndroidManualInstructions() {
  return (
    <ol className="space-y-3 mb-2 text-sm">
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">1</span>
        <span className="flex-1">Tap the <ChevronDown className="inline w-4 h-4 mx-1 rotate-180"/> <b>three-dot menu</b> in Chrome.</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">2</span>
        <span className="flex-1">Choose <b>Install app</b> (or <b>Add to Home screen</b>).</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">3</span>
        <span>Confirm. NeuroEQ launches like a native app.</span>
      </li>
      <li className="text-xs text-muted-foreground italic">
        Tip: if you previously dismissed the install prompt, you'll need to use the menu above (Chrome remembers your choice).
      </li>
    </ol>
  );
}

function DesktopInstructions() {
  return (
    <ol className="space-y-3 mb-2 text-sm">
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">1</span>
        <span className="flex-1">In Chrome/Edge, click the <Plus className="inline w-4 h-4 mx-1"/> <b>install icon</b> on the right of the address bar.</span>
      </li>
      <li className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-semibold">2</span>
        <span>Click <b>Install</b>. NeuroEQ opens in its own window.</span>
      </li>
    </ol>
  );
}
